import React, { useEffect, useRef, memo } from "react";

/**
 * TradingView Mini Symbol Overview widget.
 * Renders a compact live chart for a given FX/crypto/index symbol.
 *
 * The widget auto-resolves symbols like "XAUUSD" or "EURUSD"; you can also pass
 * fully-qualified symbols like "OANDA:XAUUSD" or "BINANCE:BTCUSDT".
 */
function MiniChartBase({ symbol = "FX:EURUSD", height = 120 }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // clean previous mount (safe DOM API, no innerHTML)
    el.replaceChildren();
    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container__widget";
    widgetDiv.style.height = "100%";
    widgetDiv.style.width = "100%";
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js";
    script.async = true;
    script.type = "text/javascript";
    // TradingView requires config as a text node inside the script tag.
    // Use textContent (safe) instead of innerHTML.
    script.textContent = JSON.stringify({
      symbol: symbol,
      width: "100%",
      height: "100%",
      locale: "en",
      dateRange: "1D",
      colorTheme: "light",
      trendLineColor: "rgba(0, 180, 255, 1)",
      underLineColor: "rgba(0, 180, 255, 0.15)",
      underLineBottomColor: "rgba(0, 180, 255, 0)",
      isTransparent: true,
      autosize: true,
      largeChartUrl: "",
      chartOnly: false,
      noTimeScale: false,
    });
    el.appendChild(widgetDiv);
    el.appendChild(script);
  }, [symbol]);

  return (
    <div
      className="tradingview-widget-container rounded-lg overflow-hidden bg-white"
      ref={containerRef}
      style={{ height }}
      data-testid={`mini-chart-${symbol}`}
    />
  );
}

// Normalise raw pair strings from admin (e.g. "XAUUSD", "EUR/USD", "btcusdt")
// into a TradingView-compatible symbol.
const SYMBOL_MAP = {
  XAUUSD: "OANDA:XAUUSD",
  GOLD:   "OANDA:XAUUSD",
  XAGUSD: "OANDA:XAGUSD",
  SILVER: "OANDA:XAGUSD",
  US30:   "TVC:DJI",
  DJI:    "TVC:DJI",
  NAS100: "TVC:NDX",
  NDX:    "TVC:NDX",
  SPX500: "TVC:SPX",
  SPX:    "TVC:SPX",
};

export function normalizeSymbol(raw) {
  if (!raw) return "FX:EURUSD";
  const s = String(raw).toUpperCase().replace(/[^A-Z0-9:]/g, "");
  if (s.includes(":")) return s;
  if (SYMBOL_MAP[s]) return SYMBOL_MAP[s];
  if (s.endsWith("USDT") || s.endsWith("BTC") || s.endsWith("ETH")) return `BINANCE:${s}`;
  // Assume FX pair by default.
  return `FX:${s}`;
}

export default memo(MiniChartBase);
